import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, GridIcon, ListIcon, StarIcon, SearchIcon, MapPinIcon } from 'lucide-react';
import SaveToFavorites from '../components/UI/SaveToFavorites';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// Mock data for vendors
const vendors = [{
  id: '1',
  name: 'Literary Apparel Co.',
  slug: 'literary-apparel-co',
  avatar: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1581318694548-0fb6e47fe59b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Premium book-themed clothing and accessories for literary enthusiasts.',
  rating: 4.8,
  reviewCount: 245,
  location: 'Portland, OR',
  categories: ['Clothing', 'Accessories'],
  isOfficial: true,
  featuredAuthors: ['Elena Richards', 'Marcus Chen'],
  socialLinks: {
    website: 'https://literaryapparel.co',
    instagram: '@literaryapparel',
    twitter: '@literaryapparel'
  }
}, {
  id: '2',
  name: 'Cosmic Creations',
  slug: 'cosmic-creations',
  avatar: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1579783902439-0f8a97d76c4f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Handcrafted bookish merchandise with a cosmic twist.',
  rating: 4.9,
  reviewCount: 189,
  location: 'Austin, TX',
  categories: ['Art Prints', 'Home & Living'],
  isOfficial: true,
  featuredAuthors: ['Amara Knight'],
  socialLinks: {
    website: 'https://cosmiccreations.co',
    instagram: '@cosmiccreations'
  }
}, {
  id: '3',
  name: 'Inkwell Press',
  slug: 'inkwell-press',
  avatar: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Beautifully crafted stationery and journals for book lovers and writers.',
  rating: 4.7,
  reviewCount: 312,
  location: 'Edinburgh, UK',
  categories: ['Stationery', 'Accessories'],
  isOfficial: false,
  featuredAuthors: [],
  socialLinks: {
    website: 'https://inkwellpress.co',
    instagram: '@inkwellpress'
  }
}, {
  id: '4',
  name: 'Chapter & Verse',
  slug: 'chapter-and-verse',
  avatar: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Curated book boxes and reading accessories for every genre and mood.',
  rating: 4.6,
  reviewCount: 178,
  location: 'London, UK',
  categories: ['Home & Living', 'Accessories'],
  isOfficial: true,
  featuredAuthors: ['Sarah J. Maas', 'Emily Henry'],
  socialLinks: {
    website: 'https://chapterandverse.co',
    instagram: '@chapterandverse'
  }
}, {
  id: '5',
  name: 'The Gilded Page',
  slug: 'the-gilded-page',
  avatar: 'https://images.unsplash.com/photo-1519682337058-a94d519337bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Luxury bookmarks, reading accessories, and limited edition art prints.',
  rating: 4.9,
  reviewCount: 94,
  location: 'New York, NY',
  categories: ['Art Prints', 'Accessories'],
  isOfficial: false,
  featuredAuthors: [],
  socialLinks: {
    instagram: '@thegildedpage'
  }
}, {
  id: '6',
  name: 'Dark Matter Designs',
  slug: 'dark-matter-designs',
  avatar: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Dark fantasy and horror-themed apparel, prints, and collectibles.',
  rating: 4.8,
  reviewCount: 267,
  location: 'Chicago, IL',
  categories: ['Clothing', 'Art Prints'],
  isOfficial: false,
  featuredAuthors: ['Stephen King'],
  socialLinks: {
    website: 'https://darkmatterdesigns.co',
    instagram: '@darkmatterdesigns'
  }
}];
type ViewMode = 'grid' | 'list';
const Vendors = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVerification, setSelectedVerification] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const filterSections: FilterSection[] = [{
    id: 'categories',
    title: 'Categories',
    type: 'checkbox',
    options: ['Clothing', 'Accessories', 'Art Prints', 'Home & Living', 'Stationery', 'Jewelry'].map(c => ({
      value: c,
      label: c
    })),
    value: selectedCategories,
    onChange: (val: string) => setSelectedCategories(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'verification',
    title: 'Verification',
    type: 'checkbox',
    options: [{
      value: 'official',
      label: 'Official Partners'
    }, {
      value: 'verified',
      label: 'Verified Sellers'
    }],
    value: selectedVerification,
    onChange: (val: string) => setSelectedVerification(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'rating',
    title: 'Rating',
    type: 'checkbox',
    options: [4, 3, 2, 1].map(r => ({
      value: String(r),
      label: `${r}+ Stars`
    })),
    value: selectedRatings,
    onChange: (val: string) => setSelectedRatings(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const handleReset = () => {
    setSelectedCategories([]);
    setSelectedVerification([]);
    setSelectedRatings([]);
    setSearchQuery('');
  };
  const filteredVendors = vendors.filter(vendor => {
    // Search filter
    if (searchQuery && !vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) && !vendor.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedCategories.length > 0 && !vendor.categories.some(c => selectedCategories.includes(c))) return false;
    if (selectedVerification.includes('official') && !vendor.isOfficial) return false;
    if (selectedRatings.length > 0) {
      const minRating = Math.min(...selectedRatings.map(Number));
      if (vendor.rating < minRating) return false;
    }
    return true;
  });
  return <div className={`min-h-screen ${dk.page}`} data-id="element-17000" data-mp="pvuj67ovann3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-17001" data-mp="13uu7tfcdr2j9">
        <div className="mb-8" data-id="element-17002" data-mp="13t0yfsadu47k">
          <div className="flex items-center gap-3 mb-2" data-id="element-17003" data-mp="ywizxa5nb23o">
            <StoreIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-17004" data-mp="ytxvqehvka2r" />
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-17005" data-mp="267exl3gi1ob9">
              Merchandise Vendors
            </h1>
          </div>
          <p className={`text-lg ${dk.textSecondary}`} data-id="element-17006" data-mp="2b9ns4490syfh">
            Discover unique literary merchandise from our curated vendors
          </p>
        </div>

        <div className="flex gap-8" data-id="element-17007" data-mp="2k76fvge1tqv2">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-17008" data-mp="2k654fat4f2ct">
            <FilterSidebar sections={filterSections} onReset={handleReset} searchable={false} data-id="element-17009" data-mp="1wi55vopki7no" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-17010" data-mp="13u980go9i9pb">
            {/* Search and View Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-17011" data-mp="18t2ngen40as4">
              <div className="relative flex-1 max-w-md" data-id="element-17012" data-mp="119jlqr635su7">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17013" data-mp="1191gx65cy6ao">
                  <SearchIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-17014" data-mp="wazy8mbug8zq" />
                </div>
                <input type="text" className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} placeholder="Search vendors..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-17015" data-mp="ra2lip9ls75m" />
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-4" data-id="element-17016" data-mp="8lntbynjun38">
                <p className={`text-sm ${dk.textMuted}`} data-id="element-17017" data-mp="3mucbw920dyb">
                  {filteredVendors.length} vendors found
                </p>
                <div className={`flex items-center border rounded-lg overflow-hidden ${dk.border}`} data-id="element-17018" data-mp="3vrohrza9hz6q">
                  <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.card} ${dk.textSecondary}`}`} title="Grid view" data-id="element-17019" data-mp="1t8uja92b155l">
                    <GridIcon className="h-5 w-5" data-id="element-17020" data-mp="230rx7nkp6b80" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.card} ${dk.textSecondary}`}`} title="List view" data-id="element-17021" data-mp="1y0mdquokzv10">
                    <ListIcon className="h-5 w-5" data-id="element-17022" data-mp="1k9hdprhax0q5" />
                  </button>
                </div>
              </div>
            </div>

            {filteredVendors.length > 0 ? <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-17023" data-mp="2vmtj46splref">
                {filteredVendors.map(vendor => <Link key={vendor.id} to={`/vendor/${vendor.slug}`} className={`rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group ${dk.card} ${viewMode === 'list' ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-17024" data-mp="2y6zjy5m111qr">
                    <div className={`relative bg-gray-200 ${viewMode === 'list' ? 'h-48 sm:h-auto sm:w-1/3' : 'h-32'}`} data-id="element-17025" data-mp="2t9preh3cc9yp">
                      <img src={vendor.banner} alt={`${vendor.name} banner`} className="w-full h-full object-cover" data-id="element-17026" data-mp="3ly4z0ik57q00" />
                      <div className={`absolute ${viewMode === 'list' ? 'top-4 left-4' : '-bottom-6 left-6'}`} data-id="element-17027" data-mp="3c70phyd8kxck">
                        <img src={vendor.avatar} alt={vendor.name} className={`w-16 h-16 rounded-full border-4 ${darkMode ? 'border-gray-700' : 'border-white'}`} data-id="element-17028" data-mp="3q5wdam8qrbo" />
                      </div>
                      <div className="absolute top-3 right-3" data-id="element-17029" data-mp="eb9jds7ves8e">
                        <SaveToFavorites itemId={vendor.id} size="sm" data-id="element-17030" data-mp="9cpylv50jmhm" />
                      </div>
                    </div>

                    <div className={`p-6 ${viewMode === 'list' ? 'flex-1 sm:pl-6' : 'pt-8'}`} data-id="element-17031" data-mp="4h0qv4iuaza8">
                      <div className="flex items-start justify-between mb-2" data-id="element-17032" data-mp="3qtz9x9doerhs">
                        <div data-id="element-17033" data-mp="3msxbgurl2jgv">
                          <h2 className={`text-xl font-bold group-hover:text-indigo-600 transition-colors ${dk.textHeading}`} data-id="element-17034" data-mp="3ms4e2keil6be">
                            {vendor.name}
                          </h2>
                          <div className={`flex items-center text-sm mt-1 ${dk.textMuted}`} data-id="element-17035" data-mp="3cwkijz3v49n6">
                            <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-17036" data-mp="2xqoi1es4ao1i" />
                            {vendor.location}
                          </div>
                        </div>
                        {vendor.isOfficial && <span className={`text-xs px-2 py-1 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`} data-id="element-17037" data-mp="37qnqlbojeeed">
                            Official Partner
                          </span>}
                      </div>

                      <p className={`text-sm line-clamp-2 mb-4 ${dk.textSecondary}`} data-id="element-17038" data-mp="2oz4mt2mouwba">
                        {vendor.description}
                      </p>

                      <div className="flex items-center justify-between mt-auto" data-id="element-17039" data-mp="1w9o08llr29mz">
                        <div className="flex items-center text-sm" data-id="element-17040" data-mp="219ab4oe3vdsm">
                          <StarIcon className="h-4 w-4 text-amber-400 fill-current mr-1" data-id="element-17041" data-mp="217h6fen1z3n9" />
                          <span className={`font-medium ${dk.textPrimary}`} data-id="element-17042" data-mp="6risiw3yc63e">
                            {vendor.rating}
                          </span>
                          <span className={`mx-1 ${dk.textMuted}`} data-id="element-17043" data-mp="gkgcz7si854n">·</span>
                          <span className={dk.textSecondary} data-id="element-17044" data-mp="bsy9abz3jqfz">
                            {vendor.reviewCount} reviews
                          </span>
                        </div>
                        <div className="flex gap-2" data-id="element-17045" data-mp="lihw1spxk4g4">
                          {vendor.categories.slice(0, 2).map(category => <span key={category} className={`text-xs px-2 py-1 rounded-full ${dk.badgeNeutral}`} data-id="element-17046" data-mp="ziiy3bhxlhx6">
                              {category}
                            </span>)}
                          {vendor.categories.length > 2 && <span className={`text-xs px-2 py-1 rounded-full ${dk.badgeNeutral}`} data-id="element-17047" data-mp="1bsi9q93kh3cp">
                              +{vendor.categories.length - 2}
                            </span>}
                        </div>
                      </div>
                    </div>
                  </Link>)}
              </div> : <div className={`text-center py-16 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`} data-id="element-17048" data-mp="t0zc6hsbahi2">
                <StoreIcon className={`h-12 w-12 mx-auto mb-4 opacity-30 ${dk.textMuted}`} data-id="element-17049" data-mp="j4w9natjsgul" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-17050" data-mp="o364tlf9vhg7">
                  No vendors found
                </h3>
                <p className={`mb-6 ${dk.textMuted}`} data-id="element-17051" data-mp="2igym413r8ayr">
                  Try adjusting your filters or search term
                </p>
                <button onClick={handleReset} className={`font-medium ${rt.accentLink}`} data-id="element-17052" data-mp="2h0t853re4f7j">
                  Clear all filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default Vendors;